import { MapPinIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { clockInSchedule, clockOutSchedule, getSchedulesForCoworkers } from 'pollworker/src/fetchers';
import { useAppDispatch, useAppSelector, useIsFeatureFlagEnabled } from 'pollworker/src/hooks';
import { selectCurrentUserIsManagerInElection, selectCurrentUserWorkSchedules } from 'pollworker/src/state/schedule';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'shared/src/components';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/src/components/ui/new';
import { selectCoworkerSchedules, setCoworkerSchedules, updateCoworkerSchedule } from '../state/timeclock';
import type { PollworkerTimeclockEntry, WorkSchedule } from '../types';
import PollworkerScheduleCardGrid from './PollworkerScheduleCardGrid';

export default PollworkerTimeclock;

function PollworkerTimeclock() {
  const dispatch = useAppDispatch();
  const timeclockEnabled = useIsFeatureFlagEnabled('pollworker-timeclock');
  const currentUserIsManagerForElection = useAppSelector(selectCurrentUserIsManagerInElection);
  const workSchedules = useAppSelector(selectCurrentUserWorkSchedules);
  const coworkerSchedules = useAppSelector(selectCoworkerSchedules);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);
  const [loadingCoworkerSchedules, setLoadingCoworkerSchedules] = useState<boolean>(false);

  const selectedSchedule = useMemo(() => {
    if (!workSchedules.length) return null;

    const fromSelection = selectedScheduleId ? workSchedules.find((schedule) => schedule.Id === selectedScheduleId) : undefined;
    if (fromSelection) return fromSelection;

    const todayString = dayjs().format('YYYY-MM-DD');
    let closestFutureScheduleIndex = workSchedules.findIndex((schedule) => schedule.HasReports && schedule.WorkDateExact >= todayString);
    if (closestFutureScheduleIndex === -1) {
      closestFutureScheduleIndex = workSchedules.length - 1;
    }
    return workSchedules[closestFutureScheduleIndex];
  }, [workSchedules, selectedScheduleId]);

  useEffect(() => {
    if (selectedSchedule) {
      loadCoworkerSchedules(selectedSchedule.Id);
    }
  }, [selectedSchedule]);

  const selectedWorkDate = useMemo(() => {
    if (selectedSchedule) {
      return formatWorkDate(selectedSchedule.WorkDateExact);
    }
    return '';
  }, [selectedSchedule]);

  const handleClockIn = useCallback(
    (schedule: WorkSchedule) => {
      return clockInSchedule(schedule.Id).then((newEntry: PollworkerTimeclockEntry) => {
        updateScheduleWithEntry(schedule, newEntry, dispatch);
      });
    },
    [dispatch],
  );

  const handleClockOut = useCallback(
    (schedule: WorkSchedule) => {
      return clockOutSchedule(schedule.Id).then((newEntry: PollworkerTimeclockEntry) => {
        updateScheduleWithEntry(schedule, newEntry, dispatch);
      });
    },
    [dispatch],
  );

  function updateScheduleWithEntry(schedule: WorkSchedule, newEntry: PollworkerTimeclockEntry, _dispatch: typeof dispatch) {
    const newSchedule: WorkSchedule = {
      ...schedule,
    };
    const indexToUpdate = newSchedule.TimeclockEntries.findIndex((entry) => entry.Id === newEntry.Id);

    if (indexToUpdate === -1) {
      newSchedule.TimeclockEntries = [newEntry];
    } else {
      newSchedule.TimeclockEntries = [...newSchedule.TimeclockEntries.slice(0, indexToUpdate), newEntry, ...newSchedule.TimeclockEntries.slice(indexToUpdate + 1)];
    }
    _dispatch(updateCoworkerSchedule(newSchedule));
  }

  function loadCoworkerSchedules(scheduleId: string) {
    setLoadingCoworkerSchedules(true);

    getSchedulesForCoworkers(scheduleId)
      .then((schedules: WorkSchedule[]) => {
        dispatch(setCoworkerSchedules(schedules));
      })
      .finally(() => {
        setLoadingCoworkerSchedules(false);
      });
  }

  function formatWorkDate(workDate: string) {
    return dayjs(workDate).format('MM/DD/YYYY');
  }

  return (
    <div className="mt-4">
      {!timeclockEnabled && (
        <div className="p-40 text-center text-xl">This feature is not enabled for your account. Contact your account manager if you believe this is a mistake.</div>
      )}
      {timeclockEnabled && !currentUserIsManagerForElection && (
        <div className="p-40 text-center text-xl">You do not have any Manager-level work assignments for this election.</div>
      )}
      {timeclockEnabled && currentUserIsManagerForElection && (
        <>
          {selectedSchedule ? (
            <Select value={selectedSchedule.Id} onValueChange={setSelectedScheduleId}>
              <SelectTrigger size="sm" data-testid="work-schedule-list" aria-label="select a work schedule" className="h-auto max-w-52 py-1.5 font-semibold">
                <MapPinIcon className="size-5 shrink-0" aria-hidden="true" />
                <SelectValue placeholder="Select schedule">
                  <span className="flex flex-col items-start text-left">
                    <span className="truncate">{selectedSchedule.VotingLocationName}</span>
                    <span className="truncate font-normal text-muted-foreground">{selectedWorkDate}</span>
                  </span>
                </SelectValue>
              </SelectTrigger>
              <SelectContent align="start" className="max-w-[14em] sm:w-[20em]">
                {workSchedules.map((schedule) => (
                  <SelectItem key={schedule.Id} value={schedule.Id} disabled={!schedule.HasReports} className={!schedule.HasReports ? 'italic text-muted-foreground' : undefined}>
                    <span className="flex flex-col items-start">
                      <span className="truncate">{schedule.VotingLocationName}</span>
                      <span className="truncate text-muted-foreground">{formatWorkDate(schedule.WorkDateExact)}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {selectedSchedule?.HasReports ? (
            <PollworkerScheduleCardGrid className="m-4 schedule-grid" pollworkerSchedules={coworkerSchedules} onClockIn={handleClockIn} onClockOut={handleClockOut} />
          ) : null}
          {selectedSchedule && !selectedSchedule.HasReports ? (
            <div className="p-40 text-center text-xl">
              Workers with the role <b>{selectedSchedule.RoleName}</b> cannot manage timeclock entries.
            </div>
          ) : null}
          <Spinner large show={loadingCoworkerSchedules} />
        </>
      )}
    </div>
  );
}
